<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <v-toolbar flat color="transparent">
          <v-toolbar-title>{{$t('Calendar.Header.fluxCalendar')}}</v-toolbar-title>
          <v-divider vertical class="mx-5"></v-divider>
          <DataFetcher v-slot:default="{ items }" endpoint="/malls" class="mr-5">
            <v-select 
              v-model="selectedMall" 
              :items="items" 
              :label="$t('Calendar.Label.mall')" 
              item-text="name" 
              color="primary" 
              value="id" 
              return-object 
              hide-details
              style="max-width: 200px;"
            ></v-select>
          </DataFetcher>
          <div class="mr-5">
            <v-select v-if="selectedMall" 
              v-model="selectedYear"
              :items="years.reverse()" 
              :label="$t('Calendar.Label.year')" 
              hide-details
              style="max-width: 150px;"
            ></v-select>
          </div>
          <div>
            <v-select v-if="selectedYear" 
              v-model="selectedMonth"
              :items="months" 
              :label="$t('Calendar.Label.month')" 
              hide-details
              style="max-width: 150px;"
            ></v-select>
          </div>
        </v-toolbar>
      </v-col>
    </v-row>
    <v-slide-x-transition mode="out-in">
      <template v-if="selectedMall && selectedYear && selectedMonth && !loading">
        <v-row v-if="calendar">
          <v-col>
            <v-calendar 
              v-model="selectedMonth"
              :weekdays="[1, 2, 3, 4, 5, 6, 0]"
              color="primary"
            >
              <template v-slot:day="{ date, year }">
                <v-text-field 
                  v-if="year === selectedYear" 
                  v-model.number="calendar[date].fluxCount"
                  :error="calendar[date].error"
                  :success="calendar[date].success"
                  :prepend-inner-icon="calendar[date].isOpen ? 'transfer_within_a_station' : 'remove_circle_outline'" 
                  class="mx-3" 
                  type="number" 
                  @change="handleInputChange(date)"
                >
                </v-text-field>
              </template>
            </v-calendar>
          </v-col>
        </v-row>
        <v-row v-else justify="center">
          <v-col cols="12" lg="6">
            <v-alert type="info" prominent icon="event_busy">
              {{ $t('Calendar.Message.notGeneratedYet', {year: selectedYear, mall: selectedMall.name}) }}.
            </v-alert>
          </v-col>
        </v-row>
      </template>
    </v-slide-x-transition>
  </v-container>
</template>

<script>
import Calendar from '@/services/calendar'
import DataFetcher from '@/components/Utils/DataFetcher'
import { eachMonthOfInterval, eachYearOfInterval, getDayOfYear, getYear, parseISO, startOfMonth, format } from 'date-fns'
import { mapState } from 'vuex'
export default {
  name: 'Flux',
  components: { DataFetcher },
  data: () => ({
    calendar: null,
    selectedMall: null,
    selectedYear: null,
    selectedMonth: null,
    loading: false
  }),

  computed: {
    ...mapState('auth', [
      'user'
    ]),
    years () {
      if (!this.selectedMall) return []
      return eachYearOfInterval({
        start: new Date(this.selectedMall.openingDate),
        end: this.selectedMall.closingDate ? new Date(this.selectedMall.closingDate) : new Date()
      })
      .map(year => getYear(year))
    },
    months () {
      if (!this.selectedYear) return []
      return eachMonthOfInterval({
        start: new Date(this.selectedYear, 0, 1),
        end: new Date(this.selectedYear, 11, 31)
      })
      .map(month => ({text: month.toLocaleString('fr-FR', {month: 'long'}), value: month}))
    }
  },

  watch: {
    selectedMall () {
      if (this.selectedMall.closingDate) {
        this.selectedYear = null
        this.selectedMonth = null
        this.calendar = []
        return
      }

      const defaultYear = getYear(new Date())
      const defaultMonth = startOfMonth(new Date())
      if (this.selectedYear === defaultYear) {
        this.loadCalendar(defaultYear)
        this.selectedMonth = defaultMonth
      } else {
        this.selectedYear = defaultYear
        this.selectedMonth = defaultMonth
      }
    },
    selectedYear (value, oldValue) {
      if (value) {
        this.loadCalendar()
        // un nouveau mois doit etre selectionner pour chaque année
        if (oldValue) this.selectedMonth = this.months[0].value
      }
    }
  },

  methods: {
    async loadCalendar () {
      this.loading = true
      let calendarDates = []

      try {
        calendarDates = await Calendar.getMallCalendar(this.selectedMall.id, this.selectedYear)
        this.calendar = null
      } catch (error) {
        this.$store.dispatch('errorMessage', this.$t('Message.Error.read'))
      }
      
      if (calendarDates.length) {
        // Convert dates array into object to facilitate calendar mapping
        this.calendar = calendarDates.reduce((obj, item) => {
          const formatDate = format(parseISO(item.date), 'yyyy-MM-dd')
          return {
            ...obj,
            [formatDate]: item,
          };
        }, {})
      }

      this.loading = false
    },
    getIndexOfDate (date) {
      return getDayOfYear(parseISO(date)) - 1
    },
    async handleInputChange (date) {
      // empty string mean 0
      if (!this.calendar[date].fluxCount) this.calendar[date].fluxCount = 0
      // integer only
      this.calendar[date].fluxCount = Math.round(this.calendar[date].fluxCount)

      const calendarDateId = this.calendar[date].id
      const fluxCount = this.calendar[date].fluxCount
      if (isNaN(fluxCount)) {
        this.calendar[date] = {...this.calendar[date], error: true, success: false}
      } else {
        try {
          await Calendar.setMallCalendarFlux(this.selectedMall.id, calendarDateId, fluxCount)
          this.calendar[date] = {...this.calendar[date], error: false, success: true}
          this.$store.dispatch('successMessage', this.$t('Message.Success.update'))
        } catch (error) {
          this.calendar[date] = {...this.calendar[date], error: true, success: false}
          this.$store.dispatch('errorMessage', this.$t('Message.Error.update'))
        }
      }
    }
  },
  created () {
    this.selectedMall = this.user.mall
  }
}
</script>